import axiosInstance from "../utils/axiosInstance";
import SeriesApi from "./series-api.service";
import { EX_END_FULL_LOCATION } from "../enums/departure_location.enum";
import { parseCurrencyString } from "../utils/helpers";
import { normalizeActiveStorageUrl } from "../utils/imageUrl";
import type { Itinerary } from "../interfaces/itinerary.interface";

const FALLBACK_IMAGE = "/iceholidays-assets/images/china_listings_cover.png";
const FALLBACK_BRAND_LOGO = "/iceholidays-assets/images/gd-logo_round.png";
const DEFAULT_LIMIT = 8;

interface TagSummary {
  id: number;
  name: string;
  description?: string;
}

interface TaggedTourPayload {
  id: string;
  attributes: {
    itinerary_id: number;
    primary_tag?: TagSummary | null;
    other_tags?: TagSummary[];
  };
}

export interface SpecialOffer {
  id: number;
  image: string;
  badge: string;
  brandLogo: string;
  brandName: string;
  category: string;
  location: string;
  from: string;
  title: string;
  guaranteed: boolean;
  originalPrice?: string;
  save?: string;
  price: string;
  href: string;
}

function formatMoney(currency: string | undefined, amount: number) {
  if (currency === "MYR") return `RM ${Math.round(amount).toLocaleString("en-US")}`;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "MYR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function categoryLogo(category: string) {
  if (category === "GD FLY CRUISE") return "/iceholidays-assets/images/icons/fly_cruise.png";
  if (category?.includes("CRUISE")) return "/iceholidays-assets/images/icons/cruise.png";
  if (category === "FS") return "/iceholidays-assets/images/icons/fs.png";
  if (category === "GD Signature") return "/iceholidays-assets/images/icons/gd_signature.png";

  return FALLBACK_BRAND_LOGO;
}

function categoryBrandName(category: string) {
  if (category === "GD FLY CRUISE") return "GD FlyCruise";
  if (category?.includes("CRUISE")) return "GD World Cruises";
  if (category === "FS") return "FS Holidays";
  if (category === "GD Signature") return "The Signature Tours";
  if (category?.includes("GD In Depth") || category?.includes("GD In-Depth")) return "GD In-Depth";

  return "GD Classic";
}

function unique<T>(items: T[]) {
  return [...new Set(items)];
}

function parsePrice(price?: string) {
  if (!price) return 0;

  const value = parseCurrencyString(price);
  return Number.isFinite(value) ? value : 0;
}

class SpecialOffersApi {
  async getOffers(type: "series" | "cruise", limit = DEFAULT_LIMIT): Promise<{ offers: SpecialOffer[]; tagIds: number[] }> {
    const taggedToursResponse = await axiosInstance.get("/b2c2b/api/v1/tagged_tours", {
      params: { per: limit },
    });
    const taggedTours: TaggedTourPayload[] = taggedToursResponse.data.data || [];
    const tagsByItineraryId = new Map<number, TagSummary>();

    taggedTours.forEach((tour) => {
      const tag = tour.attributes.primary_tag;
      if (tag && !tagsByItineraryId.has(tour.attributes.itinerary_id)) {
        tagsByItineraryId.set(tour.attributes.itinerary_id, tag);
      }
    });

    const tagIds = unique([...tagsByItineraryId.values()].map((tag) => tag.id));
    if (tagIds.length === 0) return { offers: [], tagIds: [] };

    const { itineraries } = await new SeriesApi().getItineraries({
      tags: tagIds,
      type,
      page: 1,
      per: limit,
    });

    const itinerariesById = new Map(itineraries.map((itinerary) => [itinerary.id, itinerary]));
    const offers: SpecialOffer[] = [];

    tagsByItineraryId.forEach((tag, itineraryId) => {
      const itinerary = itinerariesById.get(itineraryId);
      if (itinerary) offers.push(this.mapOffer(itinerary, tag, type));
    });

    return { offers: offers.slice(0, limit), tagIds };
  }

  private mapOffer(itinerary: Itinerary, tag: TagSummary | undefined, type: "series" | "cruise"): SpecialOffer {
    const priceValue = parsePrice(itinerary.price);
    const saveAmount = Number(itinerary.saveAmount || 0);
    const originalPrice = priceValue > 0 && saveAmount > 0
      ? formatMoney(itinerary.priceCurrency, priceValue + saveAmount)
      : undefined;
    const departureLocation = itinerary.departureLocations?.[0] || "";
    const departureLabel = EX_END_FULL_LOCATION[departureLocation] || departureLocation || "Malaysia";
    const departureDate = itinerary.departureDate?.[0];
    const query = new URLSearchParams();

    if (departureDate) query.set("depart_date", departureDate);
    if (type === "cruise") query.set("type", "cruise");

    return {
      id: itinerary.id,
      image: normalizeActiveStorageUrl(itinerary.coverImage || itinerary.images?.[0], FALLBACK_IMAGE),
      badge: tag?.name || "Special Offer",
      brandLogo: categoryLogo(itinerary.category),
      brandName: categoryBrandName(itinerary.category),
      category: itinerary.category,
      location: itinerary.country,
      from: departureLabel,
      title: itinerary.caption,
      guaranteed: (itinerary.departureDate || []).some((date) => itinerary.guranteedDepartureDates?.includes(date)),
      originalPrice,
      save: saveAmount > 0 ? formatMoney(itinerary.priceCurrency, saveAmount) : undefined,
      price: priceValue > 0 ? formatMoney(itinerary.priceCurrency, priceValue) : itinerary.price || "TBC",
      href: `/web/itinerary/${itinerary.code}${query.toString() ? `?${query}` : ""}`,
    };
  }
}

export default new SpecialOffersApi();
